import { computed, effect, inject, Injectable, signal, Signal, untracked } from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { ActorType } from '@models/api-calls/application/application.model';
import { MainSteps, PaymentSubSteps } from '@models/config/app-structure.model';
import { PrimaryPaymentMethodFormControls, SecondaryPaymentMethodFormControls } from '@models/form/form.model';
import { PaymentVerificationData } from '@models/api-calls/payment/payment-verification-warnings.model';
import { TranslateService } from '@ngx-translate/core';
import { ApplicationService } from '@services/api-calls/application.service';
import { PaymentDataService } from '@services/api-calls/payment-data.service';
import { PayerPayload, PaymentMethodPayload, PaymentService, VerifyPaymentResponse } from '@services/api-calls/payment.service';
import { ApplicationFormService } from '@services/form/application-form.service';
import { GiactForcedCodeToggleService } from '@services/testing/giact-forced-code-toggle.service';
import { VerifyTimeoutToggleService } from '@services/testing/verify-timeout-toggle.service';
import { PaymentErrorMessage, parsePaymentErrorMessage } from 'app/utils/parse-payment-error-message';
import {
    AccountTypeEnum,
    AchFormEnum,
    AddressFormEnum,
    CreditCardFormEnum,
    PaymentMethodFormEnum,
    PaymentMethodTypeEnum,
    PaymentStatusEnum,
    PaymentTypeEnum,
    PaymentVerificationEnum,
    PayorIsEnum,
    PayorOtherFormEnum,
    PrimaryPaymentMethodFormEnum,
    SecondaryPaymentMethodFormEnum,
    YesNoOptionsEnum,
} from 'app/enums/form/form.enum';
import { concatMap, EMPTY, expand, last, map, Observable, of, startWith, Subscription, switchMap, takeWhile, tap, throwError, timer } from 'rxjs';

/** Per-method failure data — preserved independently for ACH and CC so toggling
 *  the method in the UI shows only the active method's errors. */
export interface PaymentMethodFailure {
    verificationData: PaymentVerificationData | null;
    errorMessage: PaymentErrorMessage | null;
    /** True after the user clicks "Add new ACH/CC information". Suppresses the
     *  failed card and form lock for this method until the next verify attempt. */
    pendingUserRedo: boolean;
}

export interface PaymentVerificationState {
    verified: boolean;
    verifying: boolean;
    /** Method that produced the latest verify result (success or fail). */
    verifiedMethod: PaymentMethodTypeEnum | null;
    achFailure: PaymentMethodFailure | null;
    ccFailure: PaymentMethodFailure | null;
    loadedFromBackend: boolean;
}

const INITIAL_FAILURE: PaymentMethodFailure = {
    verificationData: null,
    errorMessage: null,
    pendingUserRedo: false,
};

const INITIAL_STATE: PaymentVerificationState = {
    verified: false,
    verifying: false,
    verifiedMethod: null,
    achFailure: null,
    ccFailure: null,
    loadedFromBackend: false,
};

function failureSlot(method: PaymentMethodTypeEnum): 'achFailure' | 'ccFailure' | null {
    if (method === PaymentMethodTypeEnum.ACH_EFT) return 'achFailure';
    if (method === PaymentMethodTypeEnum.CREDIT_CARD) return 'ccFailure';
    return null;
}

@Injectable({
    providedIn: 'root',
})
export class PaymentVerificationService {
    // Timeout for verification polling - if still pending, treat as success
    private static readonly VERIFICATION_TIMEOUT_MS = 60000;

    private giactForcedCodeToggle = inject(GiactForcedCodeToggleService);
    private verifyTimeoutToggle = inject(VerifyTimeoutToggleService);

    private primaryState = signal<PaymentVerificationState>({ ...INITIAL_STATE });
    private secondaryState = signal<PaymentVerificationState>({ ...INITIAL_STATE });

    /** Currently-selected method (live-bridged from PAYMENT_METHOD_TYPE). All
     *  per-method computeds (failed-card visibility, form lock, Next-button
     *  block) read these so toggling ACH ↔ CC is reactive everywhere. */
    private primaryCurrentMethod = signal<PaymentMethodTypeEnum | null>(null);
    private secondaryCurrentMethod = signal<PaymentMethodTypeEnum | null>(null);
    private primaryMethodSub: Subscription | null = null;
    private secondaryMethodSub: Subscription | null = null;

    private primaryFormGroup: FormGroup<PrimaryPaymentMethodFormControls> | null = null;
    private secondaryFormGroup: FormGroup<SecondaryPaymentMethodFormControls> | null = null;

    constructor(
        private paymentService: PaymentService,
        private applicationService: ApplicationService,
        private paymentDataService: PaymentDataService,
        private applicationFormService: ApplicationFormService,
        private translateService: TranslateService,
    ) {
        // Initialize form groups when forms are available + bridge each
        // PAYMENT_METHOD_TYPE control's value to a signal.
        effect(() => {
            const paymentMainGroup = this.applicationFormService.getMainStepFormGroup(MainSteps.PAYMENT);
            if (!paymentMainGroup) return;

            this.primaryFormGroup = paymentMainGroup.get(
                PaymentSubSteps.PRIMARY_PAYMENT_METHOD,
            ) as FormGroup<PrimaryPaymentMethodFormControls>;
            this.secondaryFormGroup = paymentMainGroup.get(
                PaymentSubSteps.SECONDARY_PAYMENT_METHOD,
            ) as FormGroup<SecondaryPaymentMethodFormControls>;

            this.bridgeMethodControl(
                this.primaryFormGroup?.get(PrimaryPaymentMethodFormEnum.PAYMENT_METHOD_GROUP) ?? null,
                this.primaryCurrentMethod,
                'primary',
            );
            this.bridgeMethodControl(
                this.secondaryFormGroup?.get(SecondaryPaymentMethodFormEnum.PAYMENT_METHOD_GROUP) ?? null,
                this.secondaryCurrentMethod,
                'secondary',
            );
        });


        // Hydrate from backend on app-payment changes. Backend tracks one method
        // at a time (whichever was last verified), so we hydrate that method's
        // failure slot only.
        effect(() => {
            const appPayment = this.paymentDataService.applicationPayment();
            if (!appPayment) return;

            this.hydrateFromBackend(
                this.primaryState,
                appPayment.primary_payment_method,
                appPayment.primary_payment_status,
                appPayment.primary_payment_verification,
                appPayment.primary_payment_verification_data,
            );

            this.hydrateFromBackend(
                this.secondaryState,
                appPayment.secondary_payment_method,
                appPayment.secondary_payment_status,
                appPayment.secondary_payment_verification,
                appPayment.secondary_payment_verification_data,
            );
        });
    }

    private hydrateFromBackend(
        stateSignal: typeof this.primaryState,
        backendMethod: string | null,
        status: string | null | undefined,
        verification: string | null | undefined,
        verificationData: PaymentVerificationData | null | undefined,
    ): void {
        const isVerified = this.isStatusVerified(status, verification);
        const method = this.normalizeMethod(backendMethod);
        const slot = method ? failureSlot(method) : null;

        const current = untracked(() => stateSignal());
        // Don't rehydrate if user is mid-redo for the relevant method (backend still has
        // stale warnings until the next verify lands).
        if (slot && current[slot]?.pendingUserRedo) return;

        const newFailureForMethod: PaymentMethodFailure | null = isVerified
            ? null
            : status === PaymentStatusEnum.FAILED
              ? { verificationData: verificationData ?? null, errorMessage: null, pendingUserRedo: false }
              : null;

        const slotValue = slot ? newFailureForMethod : null;
        const slotChanged = slot ? !this.failureEqual(current[slot], newFailureForMethod) : false;
        const verifiedChanged = isVerified !== current.verified || (isVerified && method !== current.verifiedMethod);

        if (!slotChanged && !verifiedChanged) return;

        stateSignal.set({
            ...current,
            verified: isVerified,
            verifying: false,
            verifiedMethod: isVerified ? method : current.verifiedMethod,
            achFailure: slot === 'achFailure' ? slotValue : current.achFailure,
            ccFailure: slot === 'ccFailure' ? slotValue : current.ccFailure,
            loadedFromBackend: true,
        });
    }

    private normalizeMethod(method: string | null | undefined): PaymentMethodTypeEnum | null {
        if (method === PaymentMethodTypeEnum.ACH_EFT) return PaymentMethodTypeEnum.ACH_EFT;
        if (method === PaymentMethodTypeEnum.CREDIT_CARD) return PaymentMethodTypeEnum.CREDIT_CARD;
        return null;
    }

    private failureEqual(a: PaymentMethodFailure | null, b: PaymentMethodFailure | null): boolean {
        if (a === b) return true;
        if (!a || !b) return false;
        return (
            this.verificationDataEqual(a.verificationData, b.verificationData) &&
            a.errorMessage === b.errorMessage &&
            a.pendingUserRedo === b.pendingUserRedo
        );
    }

    private isStatusVerified(status: string | null | undefined, verification: string | null | undefined): boolean {
        return (
            (status === PaymentStatusEnum.COMPLETED && verification === PaymentVerificationEnum.SUCCESS) ||
            (status === PaymentStatusEnum.SENT && verification === PaymentVerificationEnum.PENDING)
        );
    }

    private verificationDataEqual(a: PaymentVerificationData | null, b: PaymentVerificationData | null | undefined): boolean {
        const left = a ?? null;
        const right = b ?? null;
        if (left === right) return true;
        if (!left || !right) return false;
        return JSON.stringify(left) === JSON.stringify(right);
    }

    isAnyVerifying = computed(() => this.primaryState().verifying || this.secondaryState().verifying);

    getVerificationState(type: PaymentTypeEnum): Signal<PaymentVerificationState> {
        return type === PaymentTypeEnum.PRIMARY ? this.primaryState.asReadonly() : this.secondaryState.asReadonly();
    }

    /** Read-only signal of the currently-selected method (from the form). */
    getCurrentMethodSignal(type: PaymentTypeEnum): Signal<PaymentMethodTypeEnum | null> {
        return type === PaymentTypeEnum.PRIMARY ? this.primaryCurrentMethod.asReadonly() : this.secondaryCurrentMethod.asReadonly();
    }

    /** Called by primary/secondary payment-method components when their
     *  PAYMENT_METHOD_GROUP is created so the service can subscribe to the
     *  PAYMENT_METHOD_TYPE control's value. Pass `null` when the group is
     *  removed to clear the signal. */
    bindMethodControl(type: PaymentTypeEnum, methodGroup: AbstractControl | null): void {
        const targetSignal = type === PaymentTypeEnum.PRIMARY ? this.primaryCurrentMethod : this.secondaryCurrentMethod;
        const subKey = type === PaymentTypeEnum.PRIMARY ? 'primaryMethodSub' : 'secondaryMethodSub';
        this[subKey]?.unsubscribe();
        this[subKey] = null;

        if (!methodGroup) {
            targetSignal.set(null);
            return;
        }
        const ctrl = methodGroup.get(PaymentMethodFormEnum.PAYMENT_METHOD_TYPE);
        if (!ctrl) {
            targetSignal.set(null);
            return;
        }
        targetSignal.set(this.normalizeMethod(ctrl.value as string | null | undefined));
        this[subKey] = ctrl.valueChanges.pipe(startWith(ctrl.value)).subscribe((value) => {
            targetSignal.set(this.normalizeMethod(value as string | null | undefined));
        });
    }

    private bridgeMethodControl(
        methodGroup: AbstractControl | null,
        targetSignal: ReturnType<typeof signal<PaymentMethodTypeEnum | null>>,
        key: 'primary' | 'secondary',
    ): void {
        const subKey = key === 'primary' ? 'primaryMethodSub' : 'secondaryMethodSub';
        this[subKey]?.unsubscribe();
        this[subKey] = null;

        if (!methodGroup) {
            targetSignal.set(null);
            return;
        }
        const ctrl = methodGroup.get(PaymentMethodFormEnum.PAYMENT_METHOD_TYPE);
        if (!ctrl) {
            targetSignal.set(null);
            return;
        }
        targetSignal.set(this.normalizeMethod(ctrl.value as string | null | undefined));
        this[subKey] = ctrl.valueChanges.pipe(startWith(ctrl.value)).subscribe((value) => {
            targetSignal.set(this.normalizeMethod(value as string | null | undefined));
        });
    }

    isVerifying(type: PaymentTypeEnum): boolean {
        return type === PaymentTypeEnum.PRIMARY ? this.primaryState().verifying : this.secondaryState().verifying;
    }

    isVerified(type: PaymentTypeEnum): boolean {
        return type === PaymentTypeEnum.PRIMARY ? this.primaryState().verified : this.secondaryState().verified;
    }

    resetVerificationState(type: PaymentTypeEnum): void {
        const state = type === PaymentTypeEnum.PRIMARY ? this.primaryState : this.secondaryState;
        const current = state();

        // Skip reset if state was loaded from backend (first programmatic form population)
        // Flip the flag so subsequent resets (actual user changes) work normally
        if (current.loadedFromBackend) {
            state.set({ ...current, loadedFromBackend: false });
            return;
        }

        // Skip reset if backend confirms payment is verified — prevents programmatic
        // form re-population (e.g. after refreshAndWait on navigation) from clearing state
        if (this.isBackendVerified(type)) {
            return;
        }

        state.set({ ...INITIAL_STATE });
    }

    /** Per-method failure accessor used by components/computeds. */
    getMethodFailure(type: PaymentTypeEnum, method: PaymentMethodTypeEnum | null): PaymentMethodFailure | null {
        if (!method) return null;
        const state = type === PaymentTypeEnum.PRIMARY ? this.primaryState() : this.secondaryState();
        const slot = failureSlot(method);
        return slot ? state[slot] : null;
    }

    private isBackendVerified(type: PaymentTypeEnum): boolean {
        const appPayment = this.paymentDataService.applicationPayment();
        if (!appPayment) return false;

        if (type === PaymentTypeEnum.PRIMARY) {
            return this.isStatusVerified(appPayment.primary_payment_status, appPayment.primary_payment_verification);
        }

        return this.isStatusVerified(appPayment.secondary_payment_status, appPayment.secondary_payment_verification);
    }

    /**
     * Clears secondary payment data when user selects "No" for secondary payment.
     * Removes the secondary_payer role from the client (or deletes the "Other" client).
     */
    clearSecondaryPayment(): Observable<void> {
        this.secondaryState.set({ ...INITIAL_STATE });

        const applicationId = this.applicationService.applicationId();
        const secondaryPayerId = this.paymentDataService.applicationPayment()?.secondary_payer_id;
        const insuredClientId = this.applicationService.application()?.insured_client?.id;

        if (!applicationId || !secondaryPayerId) {
            return of(void 0);
        }

        const appId = Number(applicationId);

        // If secondary payer is the insured, remove the role
        if (secondaryPayerId === insuredClientId) {
            return this.paymentService.removeClientRole(appId, secondaryPayerId, ActorType.SECONDARY_PAYER).pipe(map(() => void 0));
        }

        // If secondary payer is "Other", delete the client
        return this.paymentService.deleteClient(appId, secondaryPayerId).pipe(map(() => void 0));
    }

    verifyPaymentMethod(type: PaymentTypeEnum): Observable<VerifyPaymentResponse> {
        const applicationId = this.applicationService.applicationId();
        if (!applicationId) {
            return throwError(() => new Error('No application ID available'));
        }

        const numericAppId = Number(applicationId);
        const method = this.getCurrentMethod(type);
        const stateSignal = type === PaymentTypeEnum.PRIMARY ? this.primaryState : this.secondaryState;
        const slot = method ? failureSlot(method) : null;

        // Set verifying = true. Clear THIS method's failure so a stale errorMessage
        // doesn't surface during the in-flight verify; the other method's failure
        // is left intact (per-method preservation).
        stateSignal.update((s) => ({
            ...s,
            verifying: true,
            ...(slot ? { [slot]: null } : {}),
        }));

        if (type === PaymentTypeEnum.PRIMARY) {
            return this.verifyPrimaryPayment(numericAppId, method);
        }
        return this.verifySecondaryPayment(numericAppId, method);
    }

    private getCurrentMethod(type: PaymentTypeEnum): PaymentMethodTypeEnum | null {
        const root =
            type === PaymentTypeEnum.PRIMARY
                ? this.primaryFormGroup?.get(PrimaryPaymentMethodFormEnum.PAYMENT_METHOD_GROUP)
                : this.secondaryFormGroup?.get(SecondaryPaymentMethodFormEnum.PAYMENT_METHOD_GROUP);
        const value = root?.get(PaymentMethodFormEnum.PAYMENT_METHOD_TYPE)?.value as PaymentMethodTypeEnum | null | undefined;
        return this.normalizeMethod(value as string | null | undefined);
    }

    private verifyPrimaryPayment(applicationId: number, method: PaymentMethodTypeEnum | null): Observable<VerifyPaymentResponse> {
        return this.savePrimaryPayer(applicationId).pipe(
            concatMap(() => this.createPolicyIfNeeded(applicationId)),
            concatMap(() => this.savePrimaryPaymentMethod(applicationId)),
            concatMap(() => this.pollPrimaryVerification(applicationId)),
            tap({
                next: (result) => this.applyPrimaryResult(result, method),
                error: (error) => this.applyPrimaryError(error, method),
            }),
        );
    }

    private verifySecondaryPayment(applicationId: number, method: PaymentMethodTypeEnum | null): Observable<VerifyPaymentResponse> {
        return this.saveSecondaryPayer(applicationId).pipe(
            concatMap(() => this.saveSecondaryPaymentMethod(applicationId)),
            concatMap(() => this.pollSecondaryVerification(applicationId)),
            tap({
                next: (result) => this.applySecondaryResult(result, method),
                error: (error) => this.applySecondaryError(error, method),
            }),
        );
    }

    /**
     * User clicked "Add new ACH/CC information" after a failed verification.
     * Local-only reset of THIS method's failure slot. The other method's failure
     * is preserved so toggling back to it still shows its errors.
     */
    resetForUserRedo(type: PaymentTypeEnum, method: PaymentMethodTypeEnum | null): void {
        const stateSignal = type === PaymentTypeEnum.PRIMARY ? this.primaryState : this.secondaryState;
        const slot = method ? failureSlot(method) : null;
        if (!slot) return;
        stateSignal.update((s) => ({
            ...s,
            [slot]: { ...INITIAL_FAILURE, pendingUserRedo: true },
        }));
        this.setValidControl(type, YesNoOptionsEnum.NO);
    }

    private applyPrimaryResult(result: VerifyPaymentResponse, method: PaymentMethodTypeEnum | null): void {
        const success = this.isVerifyResultSuccess(result.primary_payment_verification, result.primary_payment_status);
        this.writeVerifyResult(this.primaryState, method, success, result.primary_payment_verification_data ?? null);
        if (success) {
            this.setValidControl(PaymentTypeEnum.PRIMARY, YesNoOptionsEnum.YES);
        }
    }

    private applySecondaryResult(result: VerifyPaymentResponse, method: PaymentMethodTypeEnum | null): void {
        const success = this.isVerifyResultSuccess(result.secondary_payment_verification, result.secondary_payment_status);
        this.writeVerifyResult(this.secondaryState, method, success, result.secondary_payment_verification_data ?? null);
        if (success) {
            this.setValidControl(PaymentTypeEnum.SECONDARY, YesNoOptionsEnum.YES);
        }
    }

    private writeVerifyResult(
        stateSignal: typeof this.primaryState,
        method: PaymentMethodTypeEnum | null,
        success: boolean,
        verificationData: PaymentVerificationData | null,
    ): void {
        const slot = method ? failureSlot(method) : null;
        stateSignal.update((s) => ({
            ...s,
            verified: success,
            verifying: false,
            verifiedMethod: method ?? s.verifiedMethod,
            loadedFromBackend: false,
            ...(slot
                ? {
                      [slot]: success
                          ? null
                          : { verificationData, errorMessage: null, pendingUserRedo: false },
                  }
                : {}),
        }));
    }

    private applyPrimaryError(error: unknown, method: PaymentMethodTypeEnum | null): void {
        const errorMessage = parsePaymentErrorMessage(
            error as Parameters<typeof parsePaymentErrorMessage>[0],
            this.translateService.instant('APP_FORM.PAYMENT.PRIMARY_PAYMENT_METHOD.VERIFICATION_FAILED.TITLE.DEFAULT'),
            this.translateService.instant('APP_FORM.PAYMENT.PRIMARY_PAYMENT_METHOD.VERIFICATION_FAILED.MESSAGE.DEFAULT'),
            (key: string) => this.translateService.instant(key),
        );
        this.writeVerifyError(this.primaryState, method, errorMessage);
    }

    private applySecondaryError(error: unknown, method: PaymentMethodTypeEnum | null): void {
        const errorMessage = parsePaymentErrorMessage(
            error as Parameters<typeof parsePaymentErrorMessage>[0],
            this.translateService.instant('APP_FORM.PAYMENT.SECONDARY_PAYMENT_METHOD.VERIFICATION_FAILED.TITLE.DEFAULT'),
            this.translateService.instant('APP_FORM.PAYMENT.SECONDARY_PAYMENT_METHOD.VERIFICATION_FAILED.MESSAGE.DEFAULT'),
            (key: string) => this.translateService.instant(key),
        );
        this.writeVerifyError(this.secondaryState, method, errorMessage);
    }

    private writeVerifyError(stateSignal: typeof this.primaryState, method: PaymentMethodTypeEnum | null, errorMessage: PaymentErrorMessage): void {
        const slot = method ? failureSlot(method) : null;
        stateSignal.update((s) => ({
            ...s,
            verified: false,
            verifying: false,
            verifiedMethod: method ?? s.verifiedMethod,
            loadedFromBackend: false,
            ...(slot ? { [slot]: { verificationData: null, errorMessage, pendingUserRedo: false } } : {}),
        }));
    }

    private isVerifyResultSuccess(
        verification: PaymentVerificationEnum | undefined,
        status: PaymentStatusEnum | undefined,
    ): boolean {
        return (
            (verification === PaymentVerificationEnum.SUCCESS && status === PaymentStatusEnum.COMPLETED) ||
            verification === PaymentVerificationEnum.PENDING
        );
    }

    // ========= Primary Payment Methods =========

    private savePrimaryPayer(applicationId: number): Observable<unknown> {
        const insuredClientId = this.applicationService.application()?.insured_client?.id;
        const existingPrimaryPayerId = this.paymentDataService.applicationPayment()?.primary_payer_id;
        const payorIsValue = this.getPrimaryPayorIsValue();

        // Proposed Insured selected → add primary_payer role to insured
        if (payorIsValue === PayorIsEnum.PROPOSED_INSURED && insuredClientId) {
            // If switching from "Other" to "Insured", delete the old "Other" client first
            if (existingPrimaryPayerId && existingPrimaryPayerId !== insuredClientId) {
                return this.paymentService
                    .deleteClient(applicationId, existingPrimaryPayerId)
                    .pipe(concatMap(() => this.paymentService.addClientRole(applicationId, insuredClientId, ActorType.PRIMARY_PAYER)));
            }
            return this.paymentService.addClientRole(applicationId, insuredClientId, ActorType.PRIMARY_PAYER);
        }

        // Other selected → create or update with full payload
        if (!existingPrimaryPayerId || existingPrimaryPayerId === insuredClientId) {
            const payload = this.buildPrimaryPayerPayload();
            return this.paymentService.createPrimaryPayer(applicationId, payload);
        }

        const payload = this.buildPrimaryPayerPayload();
        return this.paymentService.updatePrimaryPayer(applicationId, payload, existingPrimaryPayerId);
    }

    private getPrimaryPayorIsValue(): PayorIsEnum | null {
        const paymentMethodGroup = this.primaryFormGroup?.get(PrimaryPaymentMethodFormEnum.PAYMENT_METHOD_GROUP);
        return (paymentMethodGroup?.get(PaymentMethodFormEnum.PAYOR_IS)?.value as PayorIsEnum) ?? null;
    }

    private createPolicyIfNeeded(applicationId: number): Observable<unknown> {
        const application = this.applicationService.application();
        const dashboardPolicyId = application?.dashboard_policy_id;

        if (dashboardPolicyId === null || dashboardPolicyId === undefined) {
            return this.paymentService.createPolicy(applicationId).pipe(
                concatMap((response) => {
                    // Refresh application to get updated dashboard_policy_id
                    this.applicationService.refreshApplicationData.update((n) => n + 1);
                    return of(response);
                }),
            );
        }

        return of(null);
    }

    private savePrimaryPaymentMethod(applicationId: number): Observable<unknown> {
        const payload = this.buildPrimaryPaymentMethodPayload();
        return this.paymentService.savePrimaryPaymentMethod(applicationId, payload, this.giactForcedCodeToggle.selectedId());
    }

    private pollPrimaryVerification(applicationId: number): Observable<VerifyPaymentResponse> {
        const startTime = Date.now();
        const forceTimeout = this.verifyTimeoutToggle.enabled();
        return this.paymentService.verifyPrimaryPaymentMethod(applicationId, forceTimeout).pipe(
            expand((response) => {
                const elapsed = Date.now() - startTime;
                if (response.primary_payment_verification === PaymentVerificationEnum.PENDING) {
                    if (elapsed >= PaymentVerificationService.VERIFICATION_TIMEOUT_MS) {
                        return EMPTY;
                    }
                    return timer(1000).pipe(switchMap(() => this.paymentService.verifyPrimaryPaymentMethod(applicationId, forceTimeout)));
                }
                return EMPTY;
            }),
            takeWhile((response) => {
                const elapsed = Date.now() - startTime;
                return (
                    response.primary_payment_verification === PaymentVerificationEnum.PENDING &&
                    elapsed < PaymentVerificationService.VERIFICATION_TIMEOUT_MS
                );
            }, true),
            last(),
        );
    }

    private buildPrimaryPayerPayload(): Omit<PayerPayload, 'role'> {
        const paymentMethodGroup = this.primaryFormGroup?.get(PrimaryPaymentMethodFormEnum.PAYMENT_METHOD_GROUP);
        const payorIsValue = paymentMethodGroup?.get(PaymentMethodFormEnum.PAYOR_IS)?.value;

        if (payorIsValue === PayorIsEnum.PROPOSED_INSURED) {
            const insuredClientId = this.applicationService.application()?.insured_client?.id;
            return {
                id: insuredClientId ?? undefined,
            };
        }

        // PayorIsEnum.OTHER
        const payorOtherGroup = paymentMethodGroup?.get(PaymentMethodFormEnum.PAYOR_OTHER_GROUP);
        const addressGroup = payorOtherGroup?.get(PayorOtherFormEnum.ADDRESS_GROUP);

        return {
            first_name: payorOtherGroup?.get(PayorOtherFormEnum.FIRST_NAME)?.value,
            middle_name: payorOtherGroup?.get(PayorOtherFormEnum.MIDDLE_NAME)?.value,
            last_name: payorOtherGroup?.get(PayorOtherFormEnum.LAST_NAME)?.value,
            relationship: payorOtherGroup?.get(PayorOtherFormEnum.RELATIONSHIP)?.value,
            address: addressGroup?.get(AddressFormEnum.ADDRESS)?.value,
            address2: addressGroup?.get(AddressFormEnum.ADDRESS2)?.value,
            city: addressGroup?.get(AddressFormEnum.CITY)?.value,
            country_code: addressGroup?.get(AddressFormEnum.COUNTRY_CODE)?.value,
            state_code: addressGroup?.get(AddressFormEnum.STATE_CODE)?.value,
            zip_code: addressGroup?.get(AddressFormEnum.ZIP_CODE)?.value,
        };
    }

    private buildPrimaryPaymentMethodPayload(): PaymentMethodPayload {
        const paymentMethodGroup = this.primaryFormGroup?.get(PrimaryPaymentMethodFormEnum.PAYMENT_METHOD_GROUP);
        const paymentMethodType = paymentMethodGroup?.get(PaymentMethodFormEnum.PAYMENT_METHOD_TYPE)?.value;

        if (paymentMethodType === PaymentMethodTypeEnum.ACH_EFT) {
            const achGroup = paymentMethodGroup?.get(PaymentMethodFormEnum.ACH_GROUP);

            return {
                primary_payment_method: PaymentMethodTypeEnum.ACH_EFT,
                gateway: {
                    ach_account_number: achGroup?.get(AchFormEnum.ACCOUNT_NUMBER)?.value,
                    ach_account_type: achGroup?.get(AchFormEnum.ACCOUNT_TYPE)?.value ?? AccountTypeEnum.CHECKING,
                    ach_routing_number: achGroup?.get(AchFormEnum.ROUTING_NUMBER)?.value,
                    ach_bank_name: achGroup?.get(AchFormEnum.BANK_NAME)?.value,
                },
            };
        }

        // PaymentMethodTypeEnum.CREDIT_CARD
        const ccGroup = paymentMethodGroup?.get(PaymentMethodFormEnum.CREDIT_CARD_GROUP);
        const expMonth = ccGroup?.get(CreditCardFormEnum.EXPIRATION_MONTH)?.value;
        const expYear = ccGroup?.get(CreditCardFormEnum.EXPIRATION_YEAR)?.value;

        return {
            primary_payment_method: PaymentMethodTypeEnum.CREDIT_CARD,
            gateway: {
                cc_card_number: ccGroup?.get(CreditCardFormEnum.CARD_NUMBER)?.value,
                cc_exp_date: `${expMonth}${expYear?.toString().slice(-2)}`,
                cc_cvv: ccGroup?.get(CreditCardFormEnum.CVV)?.value,
                cc_first_name: ccGroup?.get(CreditCardFormEnum.FIRST_NAME_ON_CARD)?.value,
                cc_last_name: ccGroup?.get(CreditCardFormEnum.LAST_NAME_ON_CARD)?.value,
                cc_avs_address: ccGroup?.get(CreditCardFormEnum.ADDRESS)?.value,
                cc_avs_zip: ccGroup?.get(CreditCardFormEnum.ZIP_CODE)?.value?.replace(/\D/g, ''),
            },
        };
    }

    // ========= Secondary Payment Methods =========

    private saveSecondaryPayer(applicationId: number): Observable<unknown> {
        const insuredClientId = this.applicationService.application()?.insured_client?.id;
        const existingSecondaryPayerId = this.paymentDataService.applicationPayment()?.secondary_payer_id;
        const payorIsValue = this.getSecondaryPayorIsValue();

        // Proposed Insured selected → add secondary_payer role to insured
        if (payorIsValue === PayorIsEnum.PROPOSED_INSURED && insuredClientId) {
            // If switching from "Other" to "Insured", delete the old "Other" client first
            if (existingSecondaryPayerId && existingSecondaryPayerId !== insuredClientId) {
                return this.paymentService
                    .deleteClient(applicationId, existingSecondaryPayerId)
                    .pipe(concatMap(() => this.paymentService.addClientRole(applicationId, insuredClientId, ActorType.SECONDARY_PAYER)));
            }
            return this.paymentService.addClientRole(applicationId, insuredClientId, ActorType.SECONDARY_PAYER);
        }

        // Other selected → create or update with full payload
        if (!existingSecondaryPayerId || existingSecondaryPayerId === insuredClientId) {
            const payload = this.buildSecondaryPayerPayload();
            return this.paymentService.createSecondaryPayer(applicationId, payload);
        }

        const payload = this.buildSecondaryPayerPayload();
        return this.paymentService.updateSecondaryPayer(applicationId, payload, existingSecondaryPayerId);
    }

    private getSecondaryPayorIsValue(): PayorIsEnum | null {
        const paymentMethodGroup = this.secondaryFormGroup?.get(SecondaryPaymentMethodFormEnum.PAYMENT_METHOD_GROUP);
        return (paymentMethodGroup?.get(PaymentMethodFormEnum.PAYOR_IS)?.value as PayorIsEnum) ?? null;
    }

    private saveSecondaryPaymentMethod(applicationId: number): Observable<unknown> {
        const payload = this.buildSecondaryPaymentMethodPayload();
        return this.paymentService.saveSecondaryPaymentMethod(applicationId, payload, this.giactForcedCodeToggle.selectedId());
    }

    private pollSecondaryVerification(applicationId: number): Observable<VerifyPaymentResponse> {
        const startTime = Date.now();
        const forceTimeout = this.verifyTimeoutToggle.enabled();
        return this.paymentService.verifySecondaryPaymentMethod(applicationId, forceTimeout).pipe(
            expand((response) => {
                const elapsed = Date.now() - startTime;
                if (response.secondary_payment_verification === PaymentVerificationEnum.PENDING) {
                    if (elapsed >= PaymentVerificationService.VERIFICATION_TIMEOUT_MS) {
                        return EMPTY;
                    }
                    return timer(1000).pipe(switchMap(() => this.paymentService.verifySecondaryPaymentMethod(applicationId, forceTimeout)));
                }
                return EMPTY;
            }),
            takeWhile((response) => {
                const elapsed = Date.now() - startTime;
                return (
                    response.secondary_payment_verification === PaymentVerificationEnum.PENDING &&
                    elapsed < PaymentVerificationService.VERIFICATION_TIMEOUT_MS
                );
            }, true),
            last(),
        );
    }

    private buildSecondaryPayerPayload(): Omit<PayerPayload, 'role'> {
        const paymentMethodGroup = this.secondaryFormGroup?.get(SecondaryPaymentMethodFormEnum.PAYMENT_METHOD_GROUP);
        const payorIsValue = paymentMethodGroup?.get(PaymentMethodFormEnum.PAYOR_IS)?.value;

        if (payorIsValue === PayorIsEnum.PROPOSED_INSURED) {
            const insuredClientId = this.applicationService.application()?.insured_client?.id;
            return {
                id: insuredClientId ?? undefined,
            };
        }

        // PayorIsEnum.OTHER
        const payorOtherGroup = paymentMethodGroup?.get(PaymentMethodFormEnum.PAYOR_OTHER_GROUP);
        const addressGroup = payorOtherGroup?.get(PayorOtherFormEnum.ADDRESS_GROUP);

        return {
            first_name: payorOtherGroup?.get(PayorOtherFormEnum.FIRST_NAME)?.value,
            middle_name: payorOtherGroup?.get(PayorOtherFormEnum.MIDDLE_NAME)?.value,
            last_name: payorOtherGroup?.get(PayorOtherFormEnum.LAST_NAME)?.value,
            relationship: payorOtherGroup?.get(PayorOtherFormEnum.RELATIONSHIP)?.value,
            address: addressGroup?.get(AddressFormEnum.ADDRESS)?.value,
            address2: addressGroup?.get(AddressFormEnum.ADDRESS2)?.value,
            city: addressGroup?.get(AddressFormEnum.CITY)?.value,
            country_code: addressGroup?.get(AddressFormEnum.COUNTRY_CODE)?.value,
            state_code: addressGroup?.get(AddressFormEnum.STATE_CODE)?.value,
            zip_code: addressGroup?.get(AddressFormEnum.ZIP_CODE)?.value,
        };
    }

    private buildSecondaryPaymentMethodPayload(): PaymentMethodPayload {
        const paymentMethodGroup = this.secondaryFormGroup?.get(SecondaryPaymentMethodFormEnum.PAYMENT_METHOD_GROUP);
        const paymentMethodType = paymentMethodGroup?.get(PaymentMethodFormEnum.PAYMENT_METHOD_TYPE)?.value;

        if (paymentMethodType === PaymentMethodTypeEnum.ACH_EFT) {
            const achGroup = paymentMethodGroup?.get(PaymentMethodFormEnum.ACH_GROUP);

            return {
                secondary_payment_method: PaymentMethodTypeEnum.ACH_EFT,
                gateway: {
                    ach_account_number: achGroup?.get(AchFormEnum.ACCOUNT_NUMBER)?.value,
                    ach_account_type: achGroup?.get(AchFormEnum.ACCOUNT_TYPE)?.value ?? AccountTypeEnum.CHECKING,
                    ach_routing_number: achGroup?.get(AchFormEnum.ROUTING_NUMBER)?.value,
                    ach_bank_name: achGroup?.get(AchFormEnum.BANK_NAME)?.value,
                },
            };
        }

        // PaymentMethodTypeEnum.CREDIT_CARD
        const ccGroup = paymentMethodGroup?.get(PaymentMethodFormEnum.CREDIT_CARD_GROUP);
        const expMonth = ccGroup?.get(CreditCardFormEnum.EXPIRATION_MONTH)?.value;
        const expYear = ccGroup?.get(CreditCardFormEnum.EXPIRATION_YEAR)?.value;

        return {
            secondary_payment_method: PaymentMethodTypeEnum.CREDIT_CARD,
            gateway: {
                cc_card_number: ccGroup?.get(CreditCardFormEnum.CARD_NUMBER)?.value,
                cc_exp_date: `${expMonth}${expYear?.toString().slice(-2)}`,
                cc_cvv: ccGroup?.get(CreditCardFormEnum.CVV)?.value,
                cc_first_name: ccGroup?.get(CreditCardFormEnum.FIRST_NAME_ON_CARD)?.value,
                cc_last_name: ccGroup?.get(CreditCardFormEnum.LAST_NAME_ON_CARD)?.value,
                cc_avs_address: ccGroup?.get(CreditCardFormEnum.ADDRESS)?.value,
                cc_avs_zip: ccGroup?.get(CreditCardFormEnum.ZIP_CODE)?.value?.replace(/\D/g, ''),
            },
        };
    }

    // ========= Common Methods =========

    private setValidControl(type: PaymentTypeEnum, value: number): void {
        if (type === PaymentTypeEnum.PRIMARY) {
            const validControl = this.primaryFormGroup?.get(PaymentMethodFormEnum.VALID);
            if (validControl) {
                validControl.setValue(value);
            }
        } else {
            const validControl = this.secondaryFormGroup?.get(PaymentMethodFormEnum.VALID);
            if (validControl) {
                validControl.setValue(value);
            }
        }
    }

}
